import { useCallback, useEffect, useRef, useState } from 'react';
import request from 'lib/request';
import type {
  DcaWorkspaceResponse,
  PromoteForecastsResponse,
} from 'types/dca-workspaces';
import type { DcaForecastSavePayload } from './decline-curve-analysis/dca-save-action';

const DCA_WORKSPACES_PATH = '/dca_workspaces';
const DCA_FORECASTS_PATH = '/wells/dca_forecasts';

/** How long to wait for the new workspace's well to reach Elasticsearch. */
const INDEXING_POLL_INTERVAL_MS = 2_000;
const INDEXING_TIMEOUT_MS = 60_000;

/** Which step the run is on, so the caller can show it. */
export type CreateAndPromoteStep =
  | 'idle'
  | 'creatingWorkspace'
  | 'savingForecast'
  | 'waitingForIndexing'
  | 'promoting'
  | 'done';

export interface CreateAndPromoteResult {
  /** The workspace the forecast was saved into. */
  workspaceHref: string;
  workspaceName: string;
}

interface ErrorResponse {
  error?: string;
  errors?: Record<string, string[]> | Array<{ message?: string }>;
}

/**
 * Pulls a readable message out of whatever the API returned.
 *
 * @param {ErrorResponse | undefined} data - The failed response's body.
 * @param {string} fallback - Message to use when the body says nothing useful.
 * @return {string} The message to show.
 */
const errorMessage = (
  data: ErrorResponse | undefined,
  fallback: string
): string => {
  if (typeof data?.error === 'string') return data.error;

  if (Array.isArray(data?.errors)) {
    const messages = data.errors.map((e) => e.message).filter(Boolean);
    if (messages.length > 0) return messages.join(', ');
  } else if (data?.errors && typeof data.errors === 'object') {
    const messages = Object.entries(data.errors).map(
      ([field, msgs]) => `${field} ${([] as string[]).concat(msgs).join(', ')}`
    );
    if (messages.length > 0) return messages.join('; ');
  }

  return fallback;
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Creates a DCA Workspace holding just this well, saves the forecast into it
 * and promotes that forecast.
 *
 * Promotion reads the workspace's wells out of Elasticsearch, and the well is
 * indexed in the background when the workspace is created, so the run waits
 * for indexing to finish before promoting.
 *
 * @param {string} uwi - The well the workspace is created for.
 * @return {object} `run` to start it, plus the current step and the result.
 */
export const useCreateWorkspaceAndPromote = (uwi: string) => {
  const [step, setStep] = useState<CreateAndPromoteStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateAndPromoteResult | null>(null);
  // The last successfully promoted forecast, used to keep the caller's button
  // disabled until its parameters change.
  const [promotedPayload, setPromotedPayload] =
    useState<DcaForecastSavePayload | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const runningRef = useRef(false);

  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    setStep('idle');
    setError(null);
    setResult(null);
    setPromotedPayload(null);
  }, []);

  const run = useCallback(
    async (payload: DcaForecastSavePayload) => {
      if (runningRef.current) return;
      runningRef.current = true;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);
      setResult(null);

      try {
        setStep('creatingWorkspace');
        const created = await request.post<DcaWorkspaceResponse, ErrorResponse>(
          DCA_WORKSPACES_PATH,
          // nameScope names the workspace after the well instead of "map_query".
          {
            dcaWorkspace: { source: 'map_query', uwis: [uwi], nameScope: uwi },
          },
          controller
        );
        if (created.type === 'abort') return;
        if (created.type !== 'success') {
          throw new Error(
            errorMessage(
              created.type === 'error' ? created.data : undefined,
              'Could not create the DCA Workspace'
            )
          );
        }
        const workspace = created.data;

        setStep('savingForecast');
        const saved = await request.post<{ id: number }, ErrorResponse>(
          DCA_FORECASTS_PATH,
          {
            dcaForecast: { ...payload, dcaWorkspaceId: workspace.id },
          },
          controller
        );
        if (saved.type === 'abort') return;
        if (saved.type !== 'success') {
          throw new Error(
            errorMessage(
              saved.type === 'error' ? saved.data : undefined,
              'Could not save the DCA Forecast'
            )
          );
        }

        setStep('waitingForIndexing');
        await waitForIndexing(workspace, controller);
        if (controller.signal.aborted) return;

        setStep('promoting');
        const promoted = await request.post<
          PromoteForecastsResponse,
          ErrorResponse
        >(
          `${DCA_WORKSPACES_PATH}/${workspace.id}/promote_forecasts`,
          {
            dcaScenarioId: payload.dcaScenarioId,
            uwis: [uwi],
            wellsWithSpecificVersionIds: [
              { uwi, wellDcaForecastId: saved.data.id },
            ],
          },
          controller
        );
        if (promoted.type === 'abort') return;
        if (promoted.type !== 'success') {
          throw new Error(
            errorMessage(
              promoted.type === 'error' ? promoted.data : undefined,
              'Could not promote the DCA Forecast'
            )
          );
        }
        if (promoted.data.promotedCount === 0) {
          throw new Error(
            'The forecast was saved but nothing was promoted. Open the workspace and promote it there.'
          );
        }

        setStep('done');
        setPromotedPayload(payload);
        setResult({
          workspaceHref: workspace.href,
          workspaceName: workspace.name,
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        setStep('idle');
        setError(
          err instanceof Error
            ? err.message
            : 'Could not create the workspace and promote the forecast'
        );
      } finally {
        runningRef.current = false;
      }
    },
    [uwi]
  );

  return {
    run,
    step,
    error,
    result,
    promotedPayload,
    reset,
    loading: step !== 'idle' && step !== 'done',
  };
};

/**
 * Waits until the workspace reports its wells indexed, or until the timeout.
 *
 * Promotion is worth attempting either way: if it runs before indexing
 * finishes it reports that nothing was promoted, which the caller shows.
 *
 * @param {DcaWorkspaceResponse} workspace - The newly created workspace.
 * @param {AbortController} controller - Aborts the wait along with the run.
 * @return {Promise<void>} Resolves once indexing is done or the wait is over.
 */
const waitForIndexing = async (
  workspace: DcaWorkspaceResponse,
  controller: AbortController
): Promise<void> => {
  if (!workspace.hasWellsIndexingInProgress) return;

  const deadline = Date.now() + INDEXING_TIMEOUT_MS;

  while (Date.now() < deadline && !controller.signal.aborted) {
    await wait(INDEXING_POLL_INTERVAL_MS);
    if (controller.signal.aborted) return;

    const latest = await request.get<DcaWorkspaceResponse>(
      workspace.href,
      controller
    );
    if ('error' in latest) continue;
    if (!latest.hasWellsIndexingInProgress) return;
  }
};
